import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CurrencyPoundIcon,
  DocumentTextIcon,
  XMarkIcon,
  BellIcon,
} from '@heroicons/react/24/outline';
import {
  Users,
  ChevronDown,
  Search as SearchIcon,
  FileText,
  Briefcase,
} from 'lucide-react';
import { useDashboardOverview, useDashboardTab } from '../hooks/useData';
import { DashboardSkeleton } from '../components/Loading/DashboardSkeleton';
import { useNotifications } from '../contexts/NotificationContext';
import { useModal } from '../contexts/ModalContext';
import NotificationTray from '../components/Notifications/NotificationTray';

export default function Dashboard() {
  const navigate = useNavigate();
  const { data: overview, isLoading: overviewLoading, error: overviewError } = useDashboardOverview();
  const todayTab = useDashboardTab('today');
  const attentionTab = useDashboardTab('attention');
  const upcomingTab = useDashboardTab('upcoming');
  const cashTab = useDashboardTab('cash');
  const complianceTab = useDashboardTab('compliance');

  const { unreadCount } = useNotifications();
  const { openModal } = useModal();
  const [showNotifications, setShowNotifications] = React.useState(false);
  const [headerSearch, setHeaderSearch] = React.useState('');
  const [activeTab, setActiveTab] = React.useState<'today' | 'attention' | 'upcoming' | 'cash' | 'compliance'>('today');
  const [isTaskMenuOpen, setIsTaskMenuOpen] = React.useState(false);
  const [documentSearch, setDocumentSearch] = React.useState('');
  const [activeKpiDrawer, setActiveKpiDrawer] = React.useState<null | 'jobs' | 'certs' | 'cash' | 'automation'>(null);
  const [prioritySearch, setPrioritySearch] = React.useState('');
  const [prioritySort, setPrioritySort] = React.useState<'risk' | 'age' | 'impact'>('risk');

  const tabs = { today: todayTab, attention: attentionTab, upcoming: upcomingTab, cash: cashTab, compliance: complianceTab } as const;
  const activeTabQuery = tabs[activeTab as keyof typeof tabs];
  const tabSignals = Array.isArray(activeTabQuery.data) ? activeTabQuery.data : [];
  const loading = overviewLoading || activeTabQuery.isLoading;
  const error = overviewError || activeTabQuery.error;
  const showErrorBanner = Boolean(error && overview);

  if (loading) return <DashboardSkeleton />;

  if (error && !overview) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-lg font-semibold mb-2">Error Loading Dashboard</div>
          <div className="text-gray-600 mb-4">{error?.message || 'Failed to load dashboard'}</div>
          <button onClick={() => window.location.reload()} className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg">Retry</button>
        </div>
      </div>
    );
  }

  const pipeline = overview?.jobPipeline || { scheduled: 0, inProgress: 0, overdue: 0, completed: 0 };
  const jobsNeedingActionCount = overview?.kpis?.jobsNeedingAction ?? 0;
  const moneyStuckTotal = overview?.kpis?.moneyStuckTotal ?? 0;
  const automationOpportunitiesCount = overview?.kpis?.automationOpportunities ?? 0;
  const totalTrackedJobs = pipeline.scheduled + pipeline.inProgress + pipeline.overdue + pipeline.completed;
  const completionRate = totalTrackedJobs > 0 ? Math.round((pipeline.completed / totalTrackedJobs) * 100) : 0;

  const jobSegments = [
    { key: 'Scheduled', label: 'Scheduled', value: pipeline.scheduled, color: '#0EA5E9' },
    { key: 'InProgress', label: 'In progress', value: pipeline.inProgress, color: '#6366F1' },
    { key: 'Blocked', label: 'Blocked', value: pipeline.overdue, color: '#F97316' },
    { key: 'Completed', label: 'Completed', value: pipeline.completed, color: '#22C55E' },
  ];

  const formatMoney = (value: number) => {
    if (!Number.isFinite(value)) return '£0';
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(value || 0);
  };

  type PriorityItemType = 'Job' | 'Invoice' | 'Certificate';
  type PriorityImpact = 'High' | 'Medium' | 'Low';
  type PriorityItem = {
    id: string; type: PriorityItemType; client: string; issue: string;
    ageLabel: string; ageSort: number; impactLabel: PriorityImpact;
    impactScore: number; riskScore: number; actionLabel: string; actionRoute: string;
  };

  const priorityItems: PriorityItem[] = tabSignals.map((signal: any) => {
    const severity = String(signal?.severity || '').toLowerCase();
    const riskScore = severity === 'high' ? 3 : severity === 'medium' ? 2 : 1;
    const impactScore = riskScore;
    const impactLabel: PriorityImpact = impactScore === 3 ? 'High' : impactScore === 2 ? 'Medium' : 'Low';
    const metaDate = signal?.meta?.dueDate || signal?.meta?.scheduledDate || signal?.meta?.expiryDate || signal?.meta?.sentAt;
    const ageLabel = metaDate ? new Date(metaDate).toLocaleDateString('en-GB') : '—';
    const ageSort = metaDate ? new Date(metaDate).getTime() : 0;
    const rawSubtitle = String(signal?.subtitle || '');
    const client = rawSubtitle.includes('•') ? rawSubtitle.split('•')[0].trim() : rawSubtitle || '—';
    let type: PriorityItemType = 'Job';
    if (signal?.entity?.invoiceId) type = 'Invoice';
    if (signal?.entity?.certificateId) type = 'Certificate';
    return { id: String(signal?.id || `signal-${Math.random()}`), type, client, issue: String(signal?.title || ''), ageLabel, ageSort, impactLabel, impactScore, riskScore, actionLabel: String(signal?.actionLabel || 'Open'), actionRoute: String(signal?.actionRoute || '/dashboard') };
  });

  const priorityFilteredItems = priorityItems.filter((item) => {
    if (!prioritySearch.trim()) return true;
    const q = prioritySearch.toLowerCase();
    return item.client.toLowerCase().includes(q) || item.issue.toLowerCase().includes(q) || item.type.toLowerCase().includes(q);
  });

  const sortedPriorityItems = [...priorityFilteredItems].sort((a, b) => {
    if (prioritySort === 'risk') { if (b.riskScore !== a.riskScore) return b.riskScore - a.riskScore; return a.ageSort - b.ageSort; }
    if (prioritySort === 'age') { if (a.ageSort !== b.ageSort) return a.ageSort - b.ageSort; return b.riskScore - a.riskScore; }
    if (b.impactScore !== a.impactScore) return b.impactScore - a.impactScore;
    return a.ageSort - b.ageSort;
  });

  const tabMeta: Record<typeof activeTab, { title: string; subtitle: string }> = {
    today: { title: 'Today', subtitle: 'What do I need to act on before the end of today?' },
    attention: { title: 'Attention required', subtitle: 'Issues that will become problems if you ignore them.' },
    upcoming: { title: 'Upcoming', subtitle: 'Work and renewals you should prepare for.' },
    cash: { title: 'Cash & admin', subtitle: 'Where money is stuck and admin is piling up.' },
    compliance: { title: 'Compliance', subtitle: 'Are you safe, legal, and covered across your portfolio?' },
  };
  const currentTabMeta = tabMeta[activeTab];

  // ── Operator strip logic ──
  type CardDef = { id: 'jobs' | 'certs' | 'cash' | 'automation'; outcome: string; exception: string | null; cta: string | null; state: 'success' | 'warning' | 'critical'; };
  const operatorCards: CardDef[] = [];
  const certOp = overview?.certOperator;

  if (certOp) {
    const { sentToday, missingData, needsReview, expiredCertificates } = certOp;
    if (needsReview > 0) {
      operatorCards.push({
        id: 'certs',
        outcome: sentToday > 0 ? `${sentToday} certificate${sentToday === 1 ? '' : 's'} issued today` : 'Certificates',
        exception:
          needsReview === 1
            ? '1 certificate is revoked — confirm records with your team'
            : `${needsReview} revoked certificates — confirm records with your team`,
        cta: 'Review compliance',
        state: 'critical',
      });
    } else if ((expiredCertificates ?? 0) > 0) {
      operatorCards.push({
        id: 'certs',
        outcome: sentToday > 0 ? `${sentToday} certificate${sentToday === 1 ? '' : 's'} issued today` : 'Certificates',
        exception:
          expiredCertificates === 1
            ? '1 certificate is past expiry — renewal may be due'
            : `${expiredCertificates} certificates are past expiry — renewal may be due`,
        cta: 'View renewals',
        state: expiredCertificates >= 3 ? 'critical' : 'warning',
      });
    } else if (missingData > 0) {
      operatorCards.push({
        id: 'certs',
        outcome: sentToday > 0 ? `${sentToday} certificate${sentToday === 1 ? '' : 's'} issued automatically today` : 'Certificates',
        exception: missingData === 1 ? '1 completed job is missing certificate data' : `${missingData} completed jobs have no certificate yet`,
        cta: missingData === 1 ? 'Complete it (30s)' : 'Fix missing data',
        state: 'warning',
      });
    } else if (sentToday > 0) {
      operatorCards.push({
        id: 'certs',
        outcome: `${sentToday} certificate${sentToday === 1 ? '' : 's'} issued & stored today`,
        exception: null,
        cta: null,
        state: 'success',
      });
    }
  }

  if (jobsNeedingActionCount > 0) {
    const running = pipeline.scheduled + pipeline.inProgress;
    operatorCards.push({ id: 'jobs', outcome: running > 0 ? `${running} job${running === 1 ? '' : 's'} tracked and running` : 'Job engine running', exception: jobsNeedingActionCount === 1 ? '1 job is overdue and still open' : `${jobsNeedingActionCount} jobs overdue — they need closing`, cta: 'Close them', state: jobsNeedingActionCount >= 3 ? 'critical' : 'warning' });
  }

  if (moneyStuckTotal > 0) {
    operatorCards.push({ id: 'cash', outcome: 'Invoice tracking active', exception: `${formatMoney(moneyStuckTotal)} outstanding — follow-up needed`, cta: 'Send reminders', state: moneyStuckTotal >= 500 ? 'critical' : 'warning' });
  }

  if (automationOpportunitiesCount > 0) {
    operatorCards.push({ id: 'automation', outcome: 'Automation engine analysed your workflow', exception: automationOpportunitiesCount === 1 ? '1 task is still manual that could run itself' : `${automationOpportunitiesCount} tasks could be automated`, cta: 'Enable them', state: 'warning' });
  }

  const escalationCards = operatorCards.filter((c) => c.state !== 'success');

  return (
    <div className="min-h-screen">

      {/* Error Banner */}
      {showErrorBanner && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-4">
          <p className="text-sm text-yellow-700">
            Unable to load live data.{' '}
            <button onClick={() => window.location.reload()} className="font-medium underline">Retry</button>
          </p>
        </div>
      )}

      {/* Page Header */}
      <div className="py-6">
        <div className="mb-6 pb-3">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-[#191919] leading-tight">{currentTabMeta.title}</h1>
              <p className="mt-1 text-xs font-normal text-[#515959]">{currentTabMeta.subtitle}</p>
            </div>
            <div className="flex flex-1 items-center justify-start gap-3 md:justify-end md:gap-4">
              {/* Header Search */}
              <div className="relative w-full max-w-xs">
                <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#686E6C]" />
                <input type="text" value={headerSearch} onChange={(e) => setHeaderSearch(e.target.value)} placeholder="Search..." className="h-9 w-full rounded-lg border border-[#CDCDCD] bg-[#FEFEFE] pl-9 pr-3 text-sm text-[#383838] placeholder:text-[#686E6C] focus:outline-none focus:ring-2 focus:ring-[#CDCDCD]/80" />
              </div>
              {/* New Task Dropdown */}
              <div className="relative">
                <button type="button" onClick={() => setIsTaskMenuOpen((o) => !o)} className="inline-flex items-center justify-center rounded-lg border border-[#CDCDCD] bg-[#FEFEFE] px-3 py-2 text-sm font-medium text-[#383838] hover:bg-[#F3F2F0] transition-colors gap-1">
                  <span>New task</span>
                  <ChevronDown className="h-4 w-4 text-[#686E6C]" />
                </button>
                {isTaskMenuOpen && (
                  <div className="absolute right-0 mt-2 w-72 rounded-xl border border-gray-200 bg-white shadow-xl z-40">
                    <div className="px-4 pt-3 pb-2"><p className="text-xs font-medium text-gray-500">Choose task</p></div>
                    <div className="px-2 pb-2 space-y-1">
                      {[
                        { label: 'Job', desc: 'Create a new job for a client', action: 'createJob', icon: <Briefcase className="h-4 w-4" />, bg: 'bg-emerald-50 text-emerald-600' },
                        { label: 'Quote', desc: 'Draft a quote for upcoming work', action: 'createQuote', icon: <FileText className="h-4 w-4" />, bg: 'bg-sky-50 text-sky-600' },
                        { label: 'Certificate', desc: 'Issue a new gas safety certificate', action: 'createCertificate', icon: <DocumentTextIcon className="h-4 w-4" />, bg: 'bg-orange-50 text-orange-500' },
                        { label: 'Client', desc: 'Add a new customer to your CRM', action: 'createClient', icon: <Users className="h-4 w-4" />, bg: 'bg-gray-50 text-gray-600' },
                      ].map((item) => (
                        <button key={item.action} type="button" onClick={() => { setIsTaskMenuOpen(false); openModal(item.action as any); }} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-gray-50 transition-colors">
                          <div className={`flex h-8 w-8 items-center justify-center rounded-full ${item.bg}`}>{item.icon}</div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">{item.label}</p>
                            <p className="text-xs text-gray-500 truncate">{item.desc}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {/* Notifications */}
              <div className="relative">
                <button type="button" onClick={() => setShowNotifications((o) => !o)} className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg border border-[#CDCDCD] bg-[#FEFEFE] text-[#383838] hover:bg-[#F3F2F0] transition-colors">
                  <BellIcon className="h-4 w-4" />
                  {unreadCount > 0 && <span className="absolute -top-1 -right-1 flex h-3 w-3 rounded-full bg-orange-500" />}
                </button>
                <NotificationTray isOpen={showNotifications} onClose={() => setShowNotifications(false)} />
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-4">
            <div className="flex gap-6 border-b border-[#E6E6E4] text-sm font-medium">
              {(['today', 'attention', 'upcoming', 'cash', 'compliance'] as const).map((tabId) => {
                const labels: Record<typeof tabId, string> = { today: 'Today', attention: 'Attention required', upcoming: 'Upcoming', cash: 'Cash & admin', compliance: 'Compliance' };
                const isActive = activeTab === tabId;
                return (
                  <button key={tabId} type="button" onClick={() => setActiveTab(tabId)} className={`relative pb-3 transition-colors ${isActive ? 'text-[#181818]' : 'text-[#686E6C] hover:text-[#383838]'}`}>
                    {labels[tabId]}
                    {isActive && <span className="absolute inset-x-0 bottom-0 h-[2px] bg-[#1B1B1B]" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="space-y-6">

        {/* ── Operator Strip ── */}
        {operatorCards.length === 0 ? (
          <div className="flex items-center gap-3 rounded-xl border border-[#E6E6E4] bg-white px-5 py-3.5">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-50 text-emerald-500 text-sm flex-shrink-0">✓</span>
            <p className="text-sm text-[#383838]"><span className="font-medium">System running clean.</span> No jobs, certificates, cash or compliance issues need your input right now.</p>
          </div>
        ) : (
          <div className="rounded-xl border border-[#E6E6E4] bg-white overflow-hidden">
            <div className="flex items-center border-b border-[#E6E6E4] px-4 py-2.5">
              {escalationCards.length > 0 ? (
                <>
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-amber-100 text-amber-600 text-[11px] flex-shrink-0 mr-2.5">⚡</span>
                  <p className="text-[12px] font-medium text-[#383838]">System handled most things — <span className="font-semibold">{escalationCards.length}</span> {escalationCards.length === 1 ? 'needs' : 'need'} your input</p>
                </>
              ) : (
                <>
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-50 text-emerald-500 text-[11px] flex-shrink-0 mr-2.5">✓</span>
                  <p className="text-[12px] font-medium text-[#383838]">System handled everything today</p>
                </>
              )}
            </div>
            <div className="flex gap-3 overflow-x-auto px-4 py-3 scrollbar-hide">
              {operatorCards.map((card, idx) => {
                const isCritical = card.state === 'critical';
                const isSuccess = card.state === 'success';
                return (
                  <div key={`operator-${idx}`} className={`relative flex-shrink-0 w-[272px] rounded-lg border p-3.5 flex flex-col gap-2.5 ${isCritical ? 'border-red-200 bg-red-50/30' : isSuccess ? 'border-emerald-200 bg-emerald-50/20' : 'border-[#E6E6E4] bg-white'}`}>
                    <p className={`text-[12px] font-medium ${isSuccess ? 'text-emerald-700' : 'text-[#686E6C]'}`}>
                      {isSuccess ? '✓ ' : ''}{card.outcome}
                    </p>
                    {card.exception && (
                      <p className={`text-[13px] leading-snug font-medium ${isCritical ? 'text-red-700' : 'text-[#2D2D2D]'}`}>{card.exception}</p>
                    )}
                    {card.cta && (
                      <button type="button" onClick={() => setActiveKpiDrawer(card.id)} className={`mt-0.5 self-start inline-flex items-center rounded-md px-3 py-1.5 text-[12px] font-medium transition-colors ${isCritical ? 'bg-[#181818] text-white hover:bg-[#2d2d2d]' : 'border border-[#CDCDCD] bg-white text-[#383838] hover:bg-[#F3F2F0]'}`}>
                        {card.cta}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Middle Row: Job Pipeline + Recent Documents ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Job Pipeline */}
          <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm flex flex-col">
            <div className="flex items-center justify-between px-5 pt-4">
              <h3 className="text-sm font-medium text-gray-900 tracking-[-0.02em]">Job Pipeline</h3>
            </div>
            <div className="px-5 pb-4 pt-3 grid grid-cols-1 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1.1fr)] gap-6 items-center flex-1">
              {totalTrackedJobs === 0 ? (
                <div className="col-span-full flex flex-col items-center justify-center py-10 px-4 text-center">
                  <p className="text-sm font-medium text-gray-900">No jobs in your pipeline yet</p>
                  <p className="mt-1 text-xs text-gray-500 max-w-sm">When you schedule work, counts and progress will appear here.</p>
                  <button
                    type="button"
                    onClick={() => openModal('createJob')}
                    className="mt-4 rounded-lg bg-gray-900 px-4 py-2 text-xs font-medium text-white hover:bg-gray-800 transition-colors"
                  >
                    Create your first job
                  </button>
                </div>
              ) : (
                <>
              <div className="flex items-center justify-center">
                <div className="relative h-40 w-40">
                  <svg viewBox="0 0 160 160" className="h-full w-full -rotate-90">
                    {(() => {
                      const radius = 70;
                      const circumference = 2 * Math.PI * radius;
                      const visible = jobSegments.filter((s) => s.value > 0);
                      if (!visible.length || totalTrackedJobs === 0) return null;
                      const gapPer = 0.04 / visible.length;
                      let offset = 0;
                      return visible.map((seg) => {
                        const raw = seg.value / totalTrackedJobs;
                        const vis = Math.max(raw - gapPer, 0);
                        const sda = `${vis * circumference} ${circumference}`;
                        const sdo = -offset * circumference;
                        offset += raw;
                        return <circle key={seg.key} cx="80" cy="80" r={radius} fill="none" stroke={seg.color} strokeWidth={20} strokeDasharray={sda} strokeDashoffset={sdo} strokeLinecap="round" className="transition-all duration-300" />;
                      });
                    })()}
                    {totalTrackedJobs === 0 && <circle cx="80" cy="80" r="70" fill="none" stroke="#E5E7EB" strokeWidth="20" />}
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl font-semibold text-gray-900">{totalTrackedJobs || 0}</span>
                    <span className="text-xs text-gray-500">total jobs</span>
                    {totalTrackedJobs > 0 && <span className="mt-1 text-[11px] text-emerald-600">{completionRate}% completed</span>}
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                {jobSegments.map((seg) => (
                  <div key={seg.key} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="h-5 w-1.5 rounded-sm" style={{ backgroundColor: seg.color }} />
                      <span className="text-gray-700">{seg.label}</span>
                    </div>
                    <span className="font-medium text-gray-900">{seg.value}</span>
                  </div>
                ))}
              </div>
                </>
              )}
            </div>
            <div className="mt-auto px-4 pb-4">
              <button
                type="button"
                onClick={() => totalTrackedJobs > 0 && navigate('/dashboard/jobs')}
                disabled={totalTrackedJobs === 0}
                className={`block w-full rounded-lg border px-4 py-2.5 text-center text-xs font-medium transition-colors ${
                  totalTrackedJobs === 0
                    ? 'border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed'
                    : 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                More details
              </button>
            </div>
          </div>

          {/* Recent Documents */}
          <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-white max-h-[400px] flex flex-col shadow-sm">
            <div className="flex items-center justify-between px-4 pt-4 pb-3 flex-wrap gap-2">
              <h3 className="text-[15px] font-medium text-gray-900 tracking-[-0.03em]">Recent Documents</h3>
              <div className="relative w-full sm:min-w-[180px] md:min-w-[220px]">
                <SearchIcon className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input type="text" value={documentSearch} onChange={(e) => setDocumentSearch(e.target.value)} placeholder="Search documents…" className="h-8 w-full rounded-lg border border-gray-200 bg-white pl-8 pr-2 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent" />
              </div>
            </div>
            <div className="px-3 pb-4 overflow-y-auto flex-1">
              <div className="flex h-24 items-center justify-center text-xs text-gray-400">No recent documents yet.</div>
            </div>
            <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent" />
          </div>
        </div>

        {/* ── Priority Action List ── */}
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 border-b border-gray-200 p-4">
            <h3 className="text-[15px] font-medium text-gray-900 tracking-[-0.03em]">Priority Action List</h3>
            <div className="flex flex-1 items-center justify-end gap-3">
              <div className="relative w-full max-w-xs">
                <SearchIcon className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input type="text" value={prioritySearch} onChange={(e) => setPrioritySearch(e.target.value)} placeholder="Search priority items..." className="h-8 w-full rounded-lg border border-gray-200 bg-white pl-8 pr-2 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent" />
              </div>
              <div className="inline-flex items-center gap-1 text-xs">
                <span className="hidden md:inline pr-1 text-[11px] text-gray-500">Sort by</span>
                {(['risk', 'age', 'impact'] as const).map((key) => (
                  <button key={key} type="button" onClick={() => setPrioritySort(key)} className={`px-2 py-1 rounded-full border text-[11px] transition-colors ${prioritySort === key ? 'border-gray-300 bg-white text-gray-900' : 'border-transparent text-gray-500 hover:bg-white'}`}>
                    {key === 'risk' ? 'Risk' : key === 'age' ? 'Age' : 'Impact'}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-white border-b border-gray-200">
                <tr className="text-[11px] font-medium text-gray-500 uppercase tracking-[0.16em]">
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Client</th>
                  <th className="px-4 py-3">Issue</th>
                  <th className="px-4 py-3">Age</th>
                  <th className="px-4 py-3">Impact</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedPriorityItems.length > 0 ? (
                  sortedPriorityItems.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 align-middle">
                        <div className="flex items-center gap-2">
                          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-50 border border-gray-200">
                            {item.type === 'Job' && <Briefcase className="h-3.5 w-3.5 text-gray-500" />}
                            {item.type === 'Invoice' && <CurrencyPoundIcon className="h-4 w-4 text-gray-500" />}
                            {item.type === 'Certificate' && <DocumentTextIcon className="h-4 w-4 text-gray-500" />}
                          </div>
                          <span className="text-xs font-medium text-gray-700">{item.type}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-middle text-sm text-gray-900">{item.client}</td>
                      <td className="px-4 py-3 align-middle text-sm text-gray-700">{item.issue}</td>
                      <td className="px-4 py-3 align-middle text-sm text-gray-700">{item.ageLabel}</td>
                      <td className="px-4 py-3 align-middle">
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium leading-tight ${item.impactLabel === 'High' ? 'border-red-200 bg-red-50 text-red-700' : item.impactLabel === 'Medium' ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
                          {item.impactLabel} impact
                        </span>
                      </td>
                      <td className="px-4 py-3 align-middle text-right">
                        <button type="button" onClick={() => navigate(item.actionRoute)} className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
                          {item.actionLabel}
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-5 py-6 text-center text-xs text-gray-400">Nothing needs your attention right now.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>

      {/* ── KPI Drawer ── */}
      {activeKpiDrawer && (
        <div className="fixed inset-0 z-40 flex justify-end bg-black/20" onClick={() => setActiveKpiDrawer(null)}>
          <div className="h-full w-full max-w-md bg-white shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <div>
                {activeKpiDrawer === 'jobs' && <><h2 className="text-sm font-semibold text-gray-900">Overdue Jobs ({jobsNeedingActionCount})</h2><p className="mt-1 text-xs text-gray-500">Jobs the system couldn't auto-close — they need your action.</p></>}
                {activeKpiDrawer === 'certs' && <><h2 className="text-sm font-semibold text-gray-900">Certificate follow-ups</h2><p className="mt-1 text-xs text-gray-500">Revoked certificates and compliance signals from your overview.</p></>}
                {activeKpiDrawer === 'cash' && <><h2 className="text-sm font-semibold text-gray-900">Outstanding Invoices ({formatMoney(moneyStuckTotal)})</h2><p className="mt-1 text-xs text-gray-500">Invoices that need manual follow-up to release cash.</p></>}
                {activeKpiDrawer === 'automation' && <><h2 className="text-sm font-semibold text-gray-900">Automation Opportunities ({automationOpportunitiesCount})</h2><p className="mt-1 text-xs text-gray-500">Tasks that could run automatically once enabled.</p></>}
              </div>
              <button type="button" onClick={() => setActiveKpiDrawer(null)} className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:bg-gray-50">
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 text-sm">
              {activeKpiDrawer === 'jobs' && (
                [...(todayTab.data || []), ...(attentionTab.data || [])].filter((s: any) => s?.entity?.jobId).slice(0, 10).map((signal: any) => (
                  <div key={signal.id} className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                    <div className="min-w-0">
                      <p className="truncate text-xs font-medium text-gray-900">{signal.title}</p>
                      <p className="text-[11px] text-gray-500 truncate">{signal.subtitle || ''}</p>
                    </div>
                    <button type="button" onClick={() => navigate(signal.actionRoute)} className="ml-2 inline-flex items-center rounded-lg bg-gray-900 px-2.5 py-1 text-[11px] font-medium text-white">{signal.actionLabel}</button>
                  </div>
                ))
              )}
              {activeKpiDrawer === 'certs' && (
                (complianceTab.data || []).filter((s: any) => s?.entity?.certificateId).slice(0, 10).map((signal: any) => (
                  <div key={signal.id} className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                    <div className="min-w-0">
                      <p className="truncate text-xs font-medium text-gray-900">{signal.title}</p>
                      <p className="text-[11px] text-gray-500 truncate">{signal.subtitle || ''}</p>
                    </div>
                    <button type="button" onClick={() => navigate(signal.actionRoute)} className="ml-2 inline-flex items-center rounded-lg bg-gray-900 px-2.5 py-1 text-[11px] font-medium text-white">{signal.actionLabel}</button>
                  </div>
                ))
              )}
              {activeKpiDrawer === 'cash' && (
                [...(attentionTab.data || []), ...(cashTab.data || [])].filter((s: any) => s?.entity?.invoiceId || s?.entity?.jobId).slice(0, 10).map((signal: any) => (
                  <div key={signal.id} className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                    <div className="min-w-0">
                      <p className="truncate text-xs font-medium text-gray-900">{signal.title}</p>
                      <p className="text-[11px] text-gray-500 truncate">{signal.subtitle || ''}</p>
                    </div>
                    <button type="button" onClick={() => navigate(signal.actionRoute)} className="ml-2 inline-flex items-center rounded-lg bg-gray-900 px-2.5 py-1 text-[11px] font-medium text-white">{signal.actionLabel}</button>
                  </div>
                ))
              )}
              {activeKpiDrawer === 'automation' && (
                <p className="text-xs text-gray-500">Automation suggestions will appear here once the engine has analysed enough workflow patterns.</p>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
